import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, IconButton, Chip, 
  CircularProgress, FormControl, InputLabel, Select, MenuItem,
  Alert, Switch, FormControlLabel
} from '@mui/material';
import { Add, Edit, Delete, Refresh, AccessTime, BusinessCenter } from '@mui/icons-material';
import api from '../services/api';
import { toast } from 'react-hot-toast';

const Shifts = () => {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    shift_type: 'general',
    start_time: '09:00',
    end_time: '18:00',
    break_start: '13:00',
    break_end: '14:00',
    total_hours: 8,
    overtime_threshold: 8,
    is_active: true,
    description: ''
  });

  useEffect(() => {
    fetchShifts();
  }, []);

  const fetchShifts = async () => {
    setLoading(true);
    try {
      const response = await api.get('/hr/shifts/');
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.results) {
        data = response.data.results;
      }
      setShifts(data);
    } catch (error) {
      toast.error('Failed to fetch shifts');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (shift = null) => {
    if (shift) {
      setEditingShift(shift);
      setFormData({
        name: shift.name || '',
        shift_type: shift.shift_type || 'general',
        start_time: shift.start_time || '09:00',
        end_time: shift.end_time || '18:00',
        break_start: shift.break_start || '13:00',
        break_end: shift.break_end || '14:00',
        total_hours: shift.total_hours || 8,
        overtime_threshold: shift.overtime_threshold || 8,
        is_active: shift.is_active !== undefined ? shift.is_active : true,
        description: shift.description || ''
      });
    } else {
      setEditingShift(null);
      setFormData({
        name: '',
        shift_type: 'general',
        start_time: '09:00',
        end_time: '18:00',
        break_start: '13:00',
        break_end: '14:00',
        total_hours: 8,
        overtime_threshold: 8,
        is_active: true,
        description: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingShift(null);
  };

  const handleSubmit = async () => {
    try {
      if (!formData.name) {
        toast.error('Shift name is required');
        return;
      }

      const submitData = {
        ...formData,
        total_hours: parseFloat(formData.total_hours) || 0,
        overtime_threshold: parseFloat(formData.overtime_threshold) || 0
      };

      if (editingShift) {
        await api.put(`/hr/shifts/${editingShift.id}/`, submitData);
        toast.success('Shift updated successfully');
      } else {
        await api.post('/hr/shifts/', submitData);
        toast.success('Shift created successfully');
      }
      handleCloseDialog();
      fetchShifts();
    } catch (error) {
      console.error('Error saving shift:', error);
      toast.error('Failed to save shift');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this shift?')) {
      try {
        await api.delete(`/hr/shifts/${id}/`);
        toast.success('Shift deleted successfully');
        fetchShifts();
      } catch (error) {
        toast.error('Failed to delete shift');
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          <AccessTime sx={{ mr: 1, verticalAlign: 'middle' }} />
          Shift Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Create Shift
        </Button>
      </Box>

      <Paper sx={{ p: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Shift Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Start Time</TableCell>
                <TableCell>End Time</TableCell>
                <TableCell>Break</TableCell>
                <TableCell align="center">Total Hours</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress size={30} />
                  </TableCell>
                </TableRow>
              ) : shifts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="textSecondary">No shifts created yet</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                shifts.map((shift) => (
                  <TableRow key={shift.id}>
                    <TableCell>
                      <Typography fontWeight="bold">{shift.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={shift.shift_type === 'general' ? 'General' : 'Night'}
                        color={shift.shift_type === 'general' ? 'primary' : 'secondary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{shift.start_time}</TableCell>
                    <TableCell>{shift.end_time}</TableCell>
                    <TableCell>
                      {shift.break_start && shift.break_end 
                        ? `${shift.break_start} - ${shift.break_end}` 
                        : 'No break'}
                    </TableCell>
                    <TableCell align="center">{shift.total_hours} hrs</TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={shift.is_active ? 'Active' : 'Inactive'}
                        color={shift.is_active ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenDialog(shift)}>
                        <Edit />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(shift.id)}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Shift Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingShift ? 'Edit Shift' : 'Create New Shift'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Shift Name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                helperText="e.g., E1, E2, E3, General, Night"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Shift Type</InputLabel>
                <Select
                  value={formData.shift_type}
                  onChange={(e) => setFormData({...formData, shift_type: e.target.value})}
                  label="Shift Type"
                >
                  <MenuItem value="general">General Shift</MenuItem>
                  <MenuItem value="night">Night Shift</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Time"
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Time"
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Break Start"
                type="time"
                value={formData.break_start}
                onChange={(e) => setFormData({...formData, break_start: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Break End"
                type="time"
                value={formData.break_end}
                onChange={(e) => setFormData({...formData, break_end: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Total Hours"
                type="number"
                value={formData.total_hours}
                onChange={(e) => setFormData({...formData, total_hours: parseFloat(e.target.value)})}
                inputProps={{ step: 0.5, min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Overtime Threshold (hours)"
                type="number"
                value={formData.overtime_threshold}
                onChange={(e) => setFormData({...formData, overtime_threshold: parseFloat(e.target.value)})}
                inputProps={{ step: 0.5, min: 0 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    color="primary"
                  />
                }
                label="Active Shift"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingShift ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Shifts;